import { useRef, useState, useEffect, useCallback } from 'react';
import * as maptilersdk from '@maptiler/sdk';

/**
 * Converts geo coordinates to viewport pixel coordinates.
 * Use this whenever you need to position a React element over a map coordinate.
 */
export function projectToScreen(
  map: maptilersdk.Map,
  coords: [number, number]
): { x: number; y: number } {
  const pt = map.project(coords as maptilersdk.LngLatLike);
  const rect = map.getContainer().getBoundingClientRect();
  return { x: pt.x + rect.left, y: pt.y + rect.top };
}

/**
 * Hook for rendering a draggable overlay element pinned to a map coordinate.
 *
 * Returns the viewport position to place the element (use with position:fixed),
 * and a pointerDown handler that drives drag via map.unproject().
 *
 * Pattern for reuse: whenever you need a React element visually locked to a
 * map coordinate but rendered outside the map container's stacking context
 * (e.g., above a modal backdrop), use this hook + createPortal.
 */
export function usePreviewMarkerOverlay(
  mapRef: React.MutableRefObject<maptilersdk.Map | null>,
  coords: [number, number] | null,
  onDrag: ((coords: [number, number]) => void) | undefined
): {
  screenPos: { x: number; y: number } | null;
  handlePointerDown: (e: React.PointerEvent) => void;
  isDragging: boolean;
} {
  const [screenPos, setScreenPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Ref mirror of isDragging — lets updateScreenPos check drag state without
  // being a stale closure dependency.
  const isDraggingRef = useRef(false);
  // Ref tracks live coordinates during drag (avoids stale closure)
  const currentCoords = useRef<[number, number] | null>(coords);

  useEffect(() => {
    currentCoords.current = coords;
  }, [coords]);

  const updateScreenPos = useCallback(() => {
    // Don't override the lifted position while the user is actively dragging.
    if (isDraggingRef.current) return;
    const map = mapRef.current;
    if (!map || !currentCoords.current) {
      setScreenPos(null);
      return;
    }
    setScreenPos(projectToScreen(map, currentCoords.current));
  }, [mapRef]);

  useEffect(() => {
    if (!coords) {
      setScreenPos(null);
      return;
    }
    const map = mapRef.current;
    if (!map) return;

    updateScreenPos();
    map.on('move', updateScreenPos);
    return () => {
      map.off('move', updateScreenPos);
    };
  }, [coords, updateScreenPos, mapRef]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Capture the pointer so move/up keep firing even if finger leaves the element
      // (important on iOS where uncaptured pointers can be silently canceled).
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);

      // Haptic feedback on drag start
      navigator.vibrate?.(50);
      isDraggingRef.current = true;
      setIsDragging(true);

      const handleMove = (moveEvent: PointerEvent) => {
        const map = mapRef.current;
        if (!map) return;
        const rect = map.getContainer().getBoundingClientRect();
        const mapX = moveEvent.clientX - rect.left;
        const mapY = moveEvent.clientY - rect.top;
        const lngLat = map.unproject([mapX, mapY] as maptilersdk.PointLike);
        currentCoords.current = [lngLat.lng, lngLat.lat];
        setScreenPos({ x: moveEvent.clientX, y: moveEvent.clientY });
      };

      const handleUp = () => {
        isDraggingRef.current = false;
        setIsDragging(false);
        if (currentCoords.current) {
          onDrag?.(currentCoords.current);
        }
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [mapRef, onDrag]
  );

  return { screenPos, handlePointerDown, isDragging };
}
